import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, Hash, Network, User as UserIcon, FileText } from "lucide-react";
import { useStore } from "@/store/useStore";
import { apiClient } from "@/lib/api-client";
import { Avatar, EmptyState } from "@/components/AppShell";
import { TagChip } from "@/components/Badge";
import type { Strain, User } from "@/types";

interface SearchResultNode {
  node: { id: string; text: string; type: string };
  strain: Strain;
}

interface SearchResults {
  strainHits: Strain[];
  nodeHits: SearchResultNode[];
  userHits: User[];
  tagHits: { tag: string; count: number }[];
}

export default function Search() {
  const strains = useStore((s) => s.strains);
  const [q, setQ] = useState("");
  const [remoteResults, setRemoteResults] = useState<any | null>(null);

  const query = q.trim().toLowerCase();

  useEffect(() => {
    if (!query) {
      setRemoteResults(null);
      return;
    }
    const timer = window.setTimeout(() => {
      apiClient.search(query).then(setRemoteResults).catch(() => setRemoteResults(null));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [query]);

  const results = useMemo<SearchResults | null>(() => {
    if (!remoteResults) return null;
    return {
      strainHits: remoteResults.strains ?? [],
      nodeHits: remoteResults.nodes ?? [],
      userHits: remoteResults.users ?? [],
      tagHits: remoteResults.tags ?? [],
    };
  }, [remoteResults]);

  const popularTags = useMemo(() => {
    const counts: Record<string, number> = {};
    strains.forEach((s) => s.tags.forEach((t) => { counts[t] = (counts[t] ?? 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [strains]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Discover</h1>
        <p className="text-[13px] text-[var(--color-ink-muted)] mt-0.5">Find strains, claims, people, and tags.</p>
      </div>

      <div className="relative mb-6">
        <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-ink-faint)]" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search ideas, claims, people, tags…"
          className="w-full h-12 pl-10 pr-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[15px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] focus:border-[var(--color-primary)] outline-none"
        />
      </div>

      {!results && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-3">popular tags</div>
          <div className="flex flex-wrap gap-2">
            {popularTags.map(([tag, count]) => (
              <TagChip key={tag} onClick={() => setQ(tag)}>{tag} · {count}</TagChip>
            ))}
          </div>
        </div>
      )}

      {results && (
        <div className="space-y-6">
          <ResultGroup title="Strains" icon={<Network size={13} />} count={results.strainHits.length}>
            {results.strainHits.map((s) => (
              <Link key={s.id} to={`/s/${s.slug}`} className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 hover:border-[var(--color-primary)] transition-colors">
                <div className="text-[14px] font-semibold text-[var(--color-ink)]">{s.title}</div>
                <div className="text-[12px] text-[var(--color-ink-muted)] line-clamp-1 mt-0.5">{s.summary}</div>
              </Link>
            ))}
          </ResultGroup>

          <ResultGroup title="Claims" icon={<FileText size={13} />} count={results.nodeHits.length}>
            {results.nodeHits.map(({ node, strain }, i) => (
              <Link key={i} to={`/s/${strain.slug}`} className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 hover:border-[var(--color-primary)] transition-colors">
                <div className="text-[13px] text-[var(--color-ink-soft)] font-serif">{node.text}</div>
                <div className="text-[11px] text-[var(--color-ink-faint)] mt-1">in {strain.title}</div>
              </Link>
            ))}
          </ResultGroup>

          <ResultGroup title="Users" icon={<UserIcon size={13} />} count={results.userHits.length}>
            {results.userHits.map((u) => (
              <Link key={u.id} to={`/u/${u.username}`} className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 hover:border-[var(--color-primary)] transition-colors">
                <Avatar initials={u.avatar_initials ?? u.username.slice(0, 2).toUpperCase()} size={28} />
                <div>
                  <div className="text-[13px] font-semibold text-[var(--color-ink)]">{u.display_name ?? u.username}</div>
                  <div className="text-[11px] text-[var(--color-ink-faint)] font-mono">@{u.username}</div>
                </div>
              </Link>
            ))}
          </ResultGroup>

          {results.strainHits.length === 0 && results.nodeHits.length === 0 && results.userHits.length === 0 && (
            <EmptyState icon={<SearchIcon size={28} />} title="No results" desc={`Nothing matched "${q}".`} />
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ title, icon, count, children }: { title: string; icon: React.ReactNode; count: number; children: React.ReactNode }) {
  if (count === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2">
        {icon} {title} ({count})
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
